class InfiniteScrollLoader extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.infinite-scroll-loader__button');
    this.loadingIndicator = this.querySelector('.infinite-scroll-loader__loading');
    this.isLoading = false;
    this.nextUrl = this.dataset.nextUrl;
    this.initialPageLoad = true;
    
    if (!this.button || !this.nextUrl) return;
    
    // Check if we need to load previous pages on initial load
    this.checkInitialPageLoad();
    
    // Button click handler
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.loadMore();
    });
    
    // Infinite scroll observer
    this.setupInfiniteScroll();
  }

  checkInitialPageLoad() {
    // Check if there's a page parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page'));
    
    if (currentPage && currentPage > 1) {
      // Load all previous pages automatically
      this.loadPreviousPages(currentPage);
    } else {
      this.initialPageLoad = false;
    }
  }

  async loadPreviousPages(targetPage) {
    // Load pages 2 through targetPage
    for (let page = 2; page <= targetPage; page++) {
      await this.loadSpecificPage(page);
    }
    this.initialPageLoad = false;
  }

  loadSpecificPage(pageNumber) {
    return new Promise((resolve) => {
      // Construct URL for specific page
      const baseUrl = window.location.pathname + window.location.search;
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('page', pageNumber);
      
      fetch(url)
        .then(response => response.text())
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const newProducts = html.querySelectorAll('#product-grid .grid__item');
          const currentGrid = document.querySelector('#product-grid');
          
          if (newProducts.length > 0 && currentGrid) {
            newProducts.forEach((product) => {
              currentGrid.appendChild(product);
            });
          }
          
          // Update next URL
          const newLoader = html.querySelector('.infinite-scroll-loader');
          if (newLoader && newLoader.dataset.nextUrl) {
            this.nextUrl = newLoader.dataset.nextUrl;
            this.dataset.nextUrl = this.nextUrl;
          }
          
          resolve();
        })
        .catch(error => {
          console.error(`Error loading page ${pageNumber}:`, error);
          resolve();
        });
    });
  }

  setupInfiniteScroll() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && !this.initialPageLoad && this.nextUrl) {
          this.loadMore();
        }
      });
    }, {
      rootMargin: '300px' // Start loading 300px before reaching the element
    });

    observer.observe(this);
  }

  loadMore() {
    if (this.isLoading || !this.nextUrl) return;
    
    this.isLoading = true;
    
    // Show loading state
    if (this.button) this.button.style.display = 'none';
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'flex';
    
    fetch(this.nextUrl)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Get new products
        const newProducts = html.querySelectorAll('#product-grid .grid__item');
        const currentGrid = document.querySelector('#product-grid');
        
        if (newProducts.length > 0 && currentGrid) {
          // Append new products with fade-in animation
          newProducts.forEach((product, index) => {
            product.classList.add('fade-in');
            product.style.animationDelay = `${index * 0.05}s`;
            currentGrid.appendChild(product);
          });
        }
        
        // Update the URL with the current page number
        this.updateUrlWithPage(this.nextUrl);
        
        // Check if there's another page
        const newLoader = html.querySelector('.infinite-scroll-loader');
        
        if (newLoader && newLoader.dataset.nextUrl) {
          // Update the next URL
          this.nextUrl = newLoader.dataset.nextUrl;
          this.dataset.nextUrl = this.nextUrl;
          
          // Reset button state
          if (this.button) this.button.style.display = '';
          if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
          this.isLoading = false;
        } else {
          // No more pages - remove the loader
          this.remove();
        }
      })
      .catch(error => {
        console.error('Error loading more products:', error);
        
        // Show button again on error
        if (this.button) this.button.style.display = '';
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        this.isLoading = false;
      });
  }

  updateUrlWithPage(nextUrl) {
    // Extract the page number from the next URL
    const url = new URL(nextUrl, window.location.origin);
    const pageParam = url.searchParams.get('page');
    
    if (pageParam) {
      // Calculate the current page (next page - 1)
      const currentPage = parseInt(pageParam) - 1;
      
      if (currentPage > 1) {
        // Update the browser URL without reloading the page
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('page', currentPage);
        window.history.replaceState({ page: currentPage }, '', newUrl);
      }
    }
  }
}

customElements.define('infinite-scroll-loader', InfiniteScrollLoader);

